import React, { useMemo } from 'react';
import { View, StyleSheet } from 'react-native';
import { Text } from '@/components/common/text';
import { useMarketplaceColors } from '@/constants/residents/marketplaceColors';
import { Hoverable } from '@/components/shared/redesign/Hoverable';
import { MarketplaceSeller } from '@/types/residents/marketplaceListing';
import { StarRating } from '../StarRating';
import { UserAvatar } from './UserAvatar';
import { SVG_COMPONENTS } from '@/assets/admin';

const ChevronRightIcon = SVG_COMPONENTS.CHEVRON_RIGHT;

interface SellerCardProps {
  seller: MarketplaceSeller;
  onPress?: () => void;
  showChevron?: boolean;
}

/**
 * Reusable seller card showing avatar, name, and rating
 */
export const SellerCard: React.FC<SellerCardProps> = ({
  seller,
  onPress,
  showChevron = true,
}) => {
  const mc = useMarketplaceColors();

  const styles = useMemo(() => StyleSheet.create({
    container: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 16,
      paddingTop: 20,
      borderTopWidth: 1,
      borderTopColor: mc.dividerGray,
    },
    info: { flex: 1, gap: 4 },
    nameRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
    name: { fontSize: 14, fontWeight: '500', color: mc.textPrimary, lineHeight: 21, flex: 1 },
    ratingRow: { flexDirection: 'row', alignItems: 'flex-end', gap: 4 },
    reviewCount: { fontSize: 12, fontWeight: '500', color: mc.textSecondary, lineHeight: 18 },
  }), [mc]);

  const content = (
    <>
      {/* Avatar */}
      <UserAvatar initials={seller.initials} size="large" />

      {/* Seller Info */}
      <View style={styles.info}>
        <View style={styles.nameRow}>
          <Text style={styles.name}>{seller.name}</Text>
          {showChevron && onPress && (
            <ChevronRightIcon width={20} height={20} color={mc.textSecondary} />
          )}
        </View>
        <View style={styles.ratingRow}>
          <StarRating rating={seller.rating} />
          <Text style={styles.reviewCount}>({seller.reviewCount})</Text>
        </View>
      </View>
    </>
  );

  return onPress ? (
    <Hoverable hover="row" style={styles.container} onPress={onPress}>
      {content}
    </Hoverable>
  ) : (
    <View style={styles.container}>{content}</View>
  );
};
